import {
  type Aabb2,
  type Point2,
  polygonArea,
  polygonContainsPoint,
  polylineBounds,
  segmentSoupContainsPoint,
  traceClosedRings,
} from '@frontend/viewer-core';

import type { CuttingClassifier } from './rolePolicy';
import type { DrawingModel, MaterialFillRing } from './types';

/**
 * Same fraction `deriveModel` hands `stitchLoops`. Face tracing has to weld
 * with the same tolerance the stitcher used, or a junction the graph already
 * split would fail to rejoin in the arrangement.
 */
const WELD_TOLERANCE_FRACTION = 1e-6;

const weldToleranceFor = (bounds: Aabb2 | null): number => {
  if (!bounds) {
    return WELD_TOLERANCE_FRACTION;
  }
  const diagonal = Math.hypot(bounds[2] - bounds[0], bounds[3] - bounds[1]);
  return diagonal > 0
    ? diagonal * WELD_TOLERANCE_FRACTION
    : WELD_TOLERANCE_FRACTION;
};

type FillContour = {
  readonly points: readonly Point2[];
  readonly bounds: Aabb2;
  readonly area: number;
};

const aabbsOverlap = (a: Aabb2, b: Aabb2): boolean =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

const aabbArea = ([minX, minY, maxX, maxY]: Aabb2): number =>
  Math.max(0, maxX - minX) * Math.max(0, maxY - minY);

/** Average of ring vertices — nesting probe, not a true centroid of area. */
const ringAnchor = (ring: readonly Point2[]): Point2 => {
  let sumX = 0;
  let sumY = 0;
  ring.forEach(([x, y]) => {
    sumX += x;
    sumY += y;
  });
  return [sumX / ring.length, sumY / ring.length];
};

/**
 * A point just left of the first edge — inside a CCW ring, outside a CW one.
 *
 * Used to classify a traced face against the closed arrangement: the slot
 * pocket of an edge-cut is a CCW face that does not nest in the notched
 * sheet, so only an even-odd test on the face-bounding segments can tell it
 * from a real island. Dangling / open cutting chains are not in that soup —
 * they do not bound a face, and counting them flips a real sheet to even.
 */
const inwardPoint = (ring: readonly Point2[]): Point2 | null => {
  const a = ring[0];
  const b = ring[1];
  if (!a || !b) {
    return null;
  }
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return ringAnchor(ring);
  }
  const inset = Math.min(length * 0.01, 1e-3) / length;
  return [(a[0] + b[0]) / 2 - dy * inset, (a[1] + b[1]) / 2 + dx * inset];
};

/** Repeats the start vertex so the closing edge is included. */
const closeRing = (points: readonly Point2[]): readonly Point2[] => {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) {
    return points;
  }
  if (first[0] === last[0] && first[1] === last[1]) {
    return points;
  }
  return [...points, first];
};

const undirectedEdgeKey = (a: Point2, b: Point2): string =>
  a[0] < b[0] || (a[0] === b[0] && a[1] <= b[1])
    ? `${a[0]},${a[1]}:${b[0]},${b[1]}`
    : `${b[0]},${b[1]}:${a[0]},${a[1]}`;

/**
 * Unique undirected edges of closed rings — each shared wall once.
 *
 * Adjacent faces both emit the wall they share; counting it twice cancels
 * the crossing and paints a slot pocket as a second sheet. Dangling cutting
 * chains never appear here, so they cannot flip a real sheet to even.
 */
const uniqueRingSegments = (
  rings: readonly (readonly Point2[])[],
): readonly (readonly [Point2, Point2])[] => {
  const seen = new Set<string>();
  const segments: Array<readonly [Point2, Point2]> = [];
  rings.forEach((points) => {
    const closed = closeRing(points);
    for (let index = 1; index < closed.length; index += 1) {
      const from = closed[index - 1];
      const to = closed[index];
      if (!from || !to) {
        continue;
      }
      if (from[0] === to[0] && from[1] === to[1]) {
        continue;
      }
      const key = undirectedEdgeKey(from, to);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      segments.push([from, to]);
    }
  });
  return segments;
};

/**
 * True when `inner` is nested inside `outer`.
 *
 * Bounding boxes only cull non-overlapping pairs. When boxes overlap, a
 * point-in-polygon test on an anchor inside `inner` decides containment —
 * AABB containment alone would mis-nest disjoint loops whose boxes nest
 * (a loop sitting in the mouth of a C-shaped outer, for example).
 */
const contourContainsContour = (
  outer: FillContour,
  inner: FillContour,
): boolean => {
  if (!aabbsOverlap(outer.bounds, inner.bounds)) {
    return false;
  }
  return polygonContainsPoint(outer.points, ringAnchor(inner.points));
};

/** Signed area of the triangle `a,b,c`; sign says which side `c` is on. */
const orientation = (a: Point2, b: Point2, c: Point2): number =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/**
 * True only for a proper interior crossing of segments `p1p2` and `p3p4` —
 * a shared endpoint or a tangent touch does not count, matching the spec's
 * `segmentsCross` (proper crossings only).
 */
const segmentsProperlyCross = (
  p1: Point2,
  p2: Point2,
  p3: Point2,
  p4: Point2,
): boolean => {
  const d1 = orientation(p3, p4, p1);
  const d2 = orientation(p3, p4, p2);
  const d3 = orientation(p1, p2, p3);
  const d4 = orientation(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
};

/** True when any edge of `a` properly crosses any edge of `b`. */
const contoursProperlyCross = (a: FillContour, b: FillContour): boolean => {
  const ringA = a.points;
  const ringB = b.points;
  for (let i = 0; i < ringA.length; i += 1) {
    const a1 = ringA[i];
    const a2 = ringA[(i + 1) % ringA.length];
    for (let j = 0; j < ringB.length; j += 1) {
      const b1 = ringB[j];
      const b2 = ringB[(j + 1) % ringB.length];
      if (a1 && a2 && b1 && b2 && segmentsProperlyCross(a1, a2, b1, b2)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Every pair of contours whose boxes overlap, via sweep-and-prune rather than
 * a brute-force all-pairs scan.
 *
 * Sorted once by box min-X, then swept: for a given contour, every other one
 * that could possibly share its box's X-range sits in a contiguous run right
 * after it in that sorted order, so the inner scan stops the moment it passes
 * that range. Both the crossing test and the containment test below need
 * exactly this candidate set.
 */
const overlappingPairs = function* (
  contours: readonly FillContour[],
): Generator<readonly [FillContour, FillContour]> {
  const byMinX = [...contours].sort((a, b) => a.bounds[0] - b.bounds[0]);
  for (let i = 0; i < byMinX.length; i += 1) {
    const a = byMinX[i];
    if (!a) {
      continue;
    }
    for (let j = i + 1; j < byMinX.length; j += 1) {
      const b = byMinX[j];
      if (!b) {
        continue;
      }
      if (b.bounds[0] > a.bounds[2]) {
        break;
      }
      if (aabbsOverlap(a.bounds, b.bounds)) {
        yield [a, b];
      }
    }
  }
};

/**
 * Even-odd material-fill rings: the translucent solid-sheet-vs-cutout hint
 * painted under the role overlay.
 *
 * Cutting chunks — not already-stitched loops — are the input. `stitchLoops`
 * stops at every degree-3 node, so a slot cut into an edge leaves the outer
 * profile open even when every branch is cutting; judging those fragments
 * whole would miss a real enclosed sheet. Face tracing rebuilds the closed
 * contours by taking the unique clockwise turn at each welded node, which is
 * well-defined at a T or K and cannot concatenate two kissing loops the way
 * a linear chain-walk can.
 *
 * A pair that properly crosses is dropped from the fill (their enclosed
 * region is undefined) without suppressing every other part in the drawing.
 *
 * Nesting is decided by a point-in-polygon test when bounding boxes overlap.
 * Depth alternates solid and hole: depth 0 is a top-level sheet (or an island
 * reappearing inside a hole at depth 2, 4, …), and each odd depth is cut into
 * its nearest even ancestor as one of its holes.
 */
export const buildMaterialFillRings = (
  model: DrawingModel,
  classifier: CuttingClassifier,
  hiddenLayers: ReadonlySet<number>,
): readonly MaterialFillRing[] | null => {
  const MIN_FILL_RING_POINTS = 3;
  const MIN_FILL_AREA = 1e-6;

  const cuttingPolylines: (readonly Point2[])[] = [];
  model.loops.forEach((loop) => {
    loop.chunks.forEach((chunk) => {
      if (
        chunk.points.length < 2 ||
        !classifier.isCutting(chunk.entityId) ||
        hiddenLayers.has(chunk.layer)
      ) {
        return;
      }
      cuttingPolylines.push(chunk.points);
    });
  });

  if (cuttingPolylines.length === 0) {
    return null;
  }

  const traced = traceClosedRings(
    cuttingPolylines,
    weldToleranceFor(model.bounds),
  );

  const contours: FillContour[] = [];
  traced.forEach((points) => {
    if (points.length < MIN_FILL_RING_POINTS) {
      return;
    }
    const bounds = polylineBounds(points);
    const area = polygonArea(points);
    if (!bounds || area <= MIN_FILL_AREA) {
      return;
    }
    contours.push({ points, bounds, area });
  });

  if (contours.length === 0) {
    return null;
  }

  const excluded = new Set<FillContour>();
  for (const [a, b] of overlappingPairs(contours)) {
    if (contoursProperlyCross(a, b)) {
      excluded.add(a);
      excluded.add(b);
    }
  }

  const eligible = contours.filter((contour) => !excluded.has(contour));
  if (eligible.length === 0) {
    return null;
  }

  const parentOf = new Map<FillContour, FillContour | null>(
    eligible.map((contour) => [contour, null]),
  );
  const parentArea = new Map<FillContour, number>();

  const considerParent = (child: FillContour, candidate: FillContour) => {
    const candidateArea = aabbArea(candidate.bounds);
    const currentArea = parentArea.get(child);
    if (currentArea === undefined || candidateArea < currentArea) {
      parentOf.set(child, candidate);
      parentArea.set(child, candidateArea);
    }
  };

  for (const [a, b] of overlappingPairs(eligible)) {
    if (contourContainsContour(a, b)) {
      considerParent(b, a);
    } else if (contourContainsContour(b, a)) {
      considerParent(a, b);
    }
  }

  const depthOf = (contour: FillContour): number => {
    let depth = 0;
    let current = parentOf.get(contour) ?? null;
    const visited = new Set<FillContour>();
    while (current) {
      if (visited.has(current)) {
        break;
      }
      visited.add(current);
      depth += 1;
      current = parentOf.get(current) ?? null;
    }
    return depth;
  };

  const directChildrenOf = new Map<FillContour, FillContour[]>();
  eligible.forEach((contour) => {
    const parent = parentOf.get(contour) ?? null;
    if (!parent) {
      return;
    }
    const children = directChildrenOf.get(parent);
    if (children) {
      children.push(contour);
    } else {
      directChildrenOf.set(parent, [contour]);
    }
  });

  const arrangementSegments = uniqueRingSegments(
    eligible.map((contour) => contour.points),
  );
  const isOddInArrangement = (contour: FillContour): boolean => {
    const probe = inwardPoint(contour.points);
    if (!probe) {
      return true;
    }
    return segmentSoupContainsPoint(arrangementSegments, probe);
  };

  const rings = eligible
    .filter((contour) => depthOf(contour) % 2 === 0)
    .filter((contour) => depthOf(contour) !== 0 || isOddInArrangement(contour))
    .map((solid) => ({
      outer: solid.points,
      holes: (directChildrenOf.get(solid) ?? []).map((child) => child.points),
    }));

  return rings.length > 0 ? rings : null;
};

type CacheEntry = {
  model: DrawingModel;
  classifier: CuttingClassifier;
  hiddenLayers: ReadonlySet<number>;
  rings: readonly MaterialFillRing[] | null;
};

/**
 * Wraps `buildMaterialFillRings` with a one-entry cache keyed by reference,
 * so an edit that never touches the cutting role skips the geometry entirely
 * instead of re-deriving it for nothing.
 *
 * This is the other half of the fix alongside `overlappingPairs`: that one
 * makes an *actual* recompute cheap; this one avoids recomputing at all when
 * `classifier` is the exact same `CuttingClassifier`
 * `createMemoizedCuttingClassifier` handed back last time — which it does
 * keep handing back, unchanged, for every edit that assigns some other role.
 * Most edits are exactly that.
 */
export const createMaterialFillBuilder = () => {
  let cache: CacheEntry | null = null;

  return (
    model: DrawingModel | null,
    classifier: CuttingClassifier,
    hiddenLayers: ReadonlySet<number>,
  ): readonly MaterialFillRing[] | null => {
    if (!model) {
      cache = null;
      return null;
    }
    if (
      cache &&
      cache.model === model &&
      cache.classifier === classifier &&
      cache.hiddenLayers === hiddenLayers
    ) {
      return cache.rings;
    }

    const rings = buildMaterialFillRings(model, classifier, hiddenLayers);
    cache = { model, classifier, hiddenLayers, rings };
    return rings;
  };
};
